"use client";

import Link from "next/link";
import type { CalendarFestival } from "@/lib/api/calendar";
import Badge from "@ishvaram/ui/Badge";
import { getTypeColor, formatDateShort, formatDayMonth, typeLabel } from "./types";

/** A calendar row; vrat-year rows also say when the fast is kept, if on another day. */
type CalendarCardRow = CalendarFestival & { fasting_date?: string | null };

interface FestivalCardProps {
  festival: CalendarCardRow;
  /**
   * The festival's own page, or null when it serves none. The calendar API
   * also lists occurrences whose pages were retired (the monthly Pradosh and
   * Sankashti dates answer 410 since 2026-09-23); those render as plain cards.
   */
  href: string | null;
}

export function FestivalCard({ festival, href }: FestivalCardProps) {
  const colors = getTypeColor(festival.type);
  const body = <FestivalCardBody festival={festival} textClass={colors.text} />;

  if (!href) {
    return (
      <div className={`block p-4 rounded-xl border ${colors.bg} ${colors.border}`}>{body}</div>
    );
  }
  return (
    <Link
      href={href}
      className={`block p-4 rounded-xl border transition-all hover:shadow-md hover:scale-[1.01] active:scale-[0.99] ${colors.bg} ${colors.border}`}
    >
      {body}
    </Link>
  );
}

function FestivalCardBody({
  festival,
  textClass,
}: {
  festival: CalendarCardRow;
  textClass: string;
}) {
  const fastOn =
    festival.fasting && festival.fasting_date && festival.fasting_date !== festival.date
      ? festival.fasting_date
      : null;
  return (
    <>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <Badge
              label={typeLabel(festival.type)}
              variant={
                festival.type === "vrat"
                  ? "success"
                  : festival.type === "festival"
                    ? "warning"
                    : "accent"
              }
            />
            {festival.fasting && <Badge label="Fasting" variant="error" />}
          </div>
          <h3 className="font-serif text-base font-bold text-ish-dark m-0 leading-tight">
            {festival.name_en}
          </h3>
          <p className="text-sm text-ish-text-muted mt-0.5 mb-1.5 font-medium">
            {festival.name_hi}
          </p>
          {fastOn ? (
            <p className="text-sm font-medium text-ish-dark m-0 mb-1">
              Fast kept on {formatDayMonth(fastOn)}
            </p>
          ) : null}
          <p className="text-sm text-ish-text-muted m-0 leading-relaxed line-clamp-2">
            {festival.description_en}
          </p>
        </div>
        <div className={`text-sm font-bold whitespace-nowrap shrink-0 ${textClass}`}>
          {formatDateShort(festival.date)}
        </div>
      </div>
    </>
  );
}
